import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/Icon';

const INITIAL = {
  first_name: '', last_name: '', email: '', phone: '',
  age: '', gender: '', height: '', height_ft: '', height_in: '',
  weight: '', body_fat: '', goal: '', fitness_level: '', medical_notes: '', status: 'active'
};

export default function AddClient() {
  const navigate = useNavigate();
  const { isImperial } = useAuth();
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function update(field) {
    return (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  function buildPayload() {
    const payload = { ...form };
    if (isImperial) {
      // Convert ft+in to total inches for storage
      const ft = parseFloat(form.height_ft) || 0;
      const inches = parseFloat(form.height_in) || 0;
      payload.height = ft > 0 || inches > 0 ? (ft * 12 + inches).toFixed(2) : '';
    }
    delete payload.height_ft;
    delete payload.height_in;
    return payload;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const client = await api.post('/clients', buildPayload());
      navigate('/clients/' + client.id);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="page-header">
        <button className="icon-btn" onClick={() => navigate('/clients')}>
          <Icon name="icon-back.png" alt="Back" />
        </button>
        <h1 style={{ flex: 1 }}>New Client</h1>
        <button className="icon-btn" form="client-form" type="submit" disabled={saving}>
          <Icon name="icon-save.png" alt="Save" />
        </button>
      </div>

      <div className="page-content">
        {error && <div className="error-msg">{error}</div>}
        <form id="client-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
            <div className="form-group">
              <label className="form-label">First Name *</label>
              <input className="form-input" value={form.first_name} onChange={update('first_name')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name *</label>
              <input className="form-input" value={form.last_name} onChange={update('last_name')} required />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={update('email')} />
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" type="tel" value={form.phone} onChange={update('phone')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
            <div className="form-group">
              <label className="form-label">Age</label>
              <input className="form-input" type="number" min="1" max="120" value={form.age} onChange={update('age')} />
            </div>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-select" value={form.gender} onChange={update('gender')}>
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="prefer_not">Prefer not to say</option>
              </select>
            </div>
          </div>

          {isImperial ? (
            <div className="form-group">
              <label className="form-label">Height (ft / in)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type="number" min="0" max="8" placeholder="5" value={form.height_ft} onChange={update('height_ft')} style={{ paddingRight: 32 }} />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', fontSize: 13 }}>ft</span>
                </div>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type="number" min="0" max="11" placeholder="10" value={form.height_in} onChange={update('height_in')} style={{ paddingRight: 32 }} />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', fontSize: 13 }}>in</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Height (cm)</label>
              <input className="form-input" type="number" step="0.1" placeholder="175" value={form.height} onChange={update('height')} />
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
            <div className="form-group">
              <label className="form-label">Weight ({isImperial ? 'lbs' : 'kg'})</label>
              <input className="form-input" type="number" step="0.1" value={form.weight} onChange={update('weight')} />
            </div>
            <div className="form-group">
              <label className="form-label">Body Fat %</label>
              <input className="form-input" type="number" step="0.1" value={form.body_fat} onChange={update('body_fat')} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Fitness Level</label>
            <select className="form-select" value={form.fitness_level} onChange={update('fitness_level')}>
              <option value="">Select</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" value={form.status} onChange={update('status')}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="lead">Lead</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Goal</label>
            <textarea className="form-textarea" value={form.goal} onChange={update('goal')} placeholder="e.g. Lose 10kg, run a marathon..." />
          </div>
          <div className="form-group">
            <label className="form-label">Medical Notes</label>
            <textarea className="form-textarea" value={form.medical_notes} onChange={update('medical_notes')} placeholder="Any injuries, conditions or limitations..." />
          </div>
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Saving...</> : 'Save Client'}
          </button>
        </form>
      </div>
    </div>
  );
}
